/** @jsxImportSource @emotion/react */
import React from 'react';
import { Card, Cascader, Input, Space, Typography } from 'antd';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import type { CropTaxonomyTree } from '../../models/Crop';

const { Paragraph } = Typography;

type CascaderOption = {
  value: string;
  label: string;
  children?: CascaderOption[];
};

interface TaxonomySelectorProps {
  taxonomyOptions: CascaderOption[];
  selectedPath: string[];
  onPathChange: (path: string[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  disabled?: boolean;
}

const buildLabel = (name?: string | null, latinName?: string | null) => {
  if (name && latinName) {
    return `${name} (${latinName})`;
  }
  return name || latinName || '';
};

export const buildTaxonomyOptions = (tree?: CropTaxonomyTree): CascaderOption[] => {
  if (!tree?.families) return [];
  return tree.families.map((family) => ({
    value: String(family.id),
    label: buildLabel(family.name, family.latinName),
    children: (family.genera ?? []).map((genus) => ({
      value: String(genus.id),
      label: buildLabel(genus.name, genus.latinName),
      children: (genus.species ?? []).map((species) => ({
        value: String(species.id),
        label: buildLabel(species.name, species.latinName),
        children: (species.cultivars ?? []).map((cultivar) => ({
          value: String(cultivar.id),
          label: buildLabel(cultivar.name, cultivar.latinName),
        })),
      })),
    })),
  }));
};

export const getFullTaxonomyPath = (tree?: CropTaxonomyTree, selectedPath?: string[]): string | null => {
  if (!tree?.families || !selectedPath || selectedPath.length === 0) return null;
  
  const path: string[] = [];
  const [familyId, genusId, speciesId, cultivarId] = selectedPath.map(String);
  
  const family = tree.families.find((f) => String(f.id) === familyId);
  if (!family) return null;
  path.push(buildLabel(family.name, family.latinName));
  
  if (genusId) {
    const genus = family.genera?.find((g) => String(g.id) === genusId);
    if (!genus) return path.join(' → ');
    path.push(buildLabel(genus.name, genus.latinName));
    
    if (speciesId) {
      const species = genus.species?.find((s) => String(s.id) === speciesId);
      if (!species) return path.join(' → ');
      path.push(buildLabel(species.name, species.latinName));
      
      if (cultivarId) {
        const cultivar = species.cultivars?.find((c) => String(c.id) === cultivarId);
        if (!cultivar) return path.join(' → ');
        path.push(buildLabel(cultivar.name, cultivar.latinName));
      }
    }
  }
  
  return path.join(' → ');
};

export const TaxonomySelector: React.FC<TaxonomySelectorProps> = ({
  taxonomyOptions,
  selectedPath,
  onPathChange,
  search,
  onSearchChange,
  loading,
  disabled,
}) => {
  return (
    <Card title="Назначение атрибутов">
      <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
        <Paragraph type="secondary" css={css({ marginBottom: 0 })}>
          Выберите уровень таксономии, чтобы переопределить значения атрибутов для культуры, вида или рода в контексте этой фермы.
        </Paragraph>
        <Cascader
          css={css({ maxWidth: 520 })}
          options={taxonomyOptions}
          onChange={(value) => onPathChange(Array.isArray(value) ? (value as string[]) : [])}
          value={selectedPath.length ? selectedPath : undefined}
          allowClear
          placeholder={loading ? 'Загрузка...' : 'Например, Piceaceae → Picea → Picea pungens'}
          showSearch
          changeOnSelect
          displayRender={(labels) => labels.filter(Boolean).join(' / ')}
          disabled={disabled || loading}
        />
        <Input.Search
          placeholder="Фильтр по названию"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          allowClear
          css={css({ maxWidth: 320 })}
          disabled={disabled}
        />
      </Space>
    </Card>
  );
};